import { createFeatureSelector, createSelector } from '@ngrx/store';
import filter from 'lodash/filter';
import { LoadStatus } from '@capital-access/common/utils';
import {
  ReportSection,
  ReportsTemplateListType,
  ReportTemplate,
  ReportTemplateFormType,
  TEMPLATE_LIBRARY_FEATURE_KEY,
  TemplateLibraryState
} from '@capital-access/reporting/common';
import { TEMPLATE_FORM_STORAGE_KEY } from '../constants';

const templateLibraryFeatureSelector = createFeatureSelector<TemplateLibraryState>(TEMPLATE_LIBRARY_FEATURE_KEY);

export const selectTemplatesList = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.templatesList
);

export const getLoadStatus = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.loadingState
);

export const getReportingTemplateRequest = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.reportTemplateFormData || sessionStorage.getItem(TEMPLATE_FORM_STORAGE_KEY)
);

export const getTemplateDrawerAction = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.actionType
);

export const getSelectedTemplateReportSections = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => {
    let filteredSections = state.reportSections;

    if (!state.showCombinedOwnership) {
      filteredSections = filteredSections.filter(
        section => section.config.reportSection !== ReportSection.CombinedOwnership
      );
    }

    if (!state.showNotesFeature) {
      filteredSections = filteredSections.filter(section => section.config.reportSection !== ReportSection.Notes);
    }

    return filteredSections;
  }
);

export const getSelectedTemplatePageSize = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.templatePageSize
);

export const getReportData = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.reportData
);

export const getButtonType = createSelector(templateLibraryFeatureSelector, (state: TemplateLibraryState) => {
  switch (state.actionType) {
    case ReportTemplateFormType.create:
      return 'saveAndOpen';
    case ReportTemplateFormType.edit:
    case ReportTemplateFormType.duplicateWithoutContinue:
      return 'save';
    case ReportTemplateFormType.duplicate:
      return 'saveAndOpen';
    default:
      return '';
  }
});

export const getActiveReportingTemplatesListType = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.activeReportingTemplatesListType
);

export const hasNoSearchResults = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.searchValue !== '' && state.templatesList?.length === 0
);

export const getSearchText = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.searchValue
);

export const getSearchResultsCount = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.templatesList?.length
);

export const getSaveDrawerLoadStatus = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.saveDrawerLoadStatus
);

export const getSavedReportTemplateId = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.savedTemplateId
);

export const getSelectedTemplate = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.selectedTemplate
);

export const getSelectedTemplateForPreview = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.selectedTemplateForPreview
);

export const getTemplateListInitialLoading = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.loadingState === LoadStatus.Loading
);

export const getTemplateListEmptyResult = createSelector(
  templateLibraryFeatureSelector,
  state =>
    state.loadingState === LoadStatus.Loaded &&
    (state.originalTemplatesList?.length === 0 ||
      (state.activeReportingTemplatesListType === ReportsTemplateListType.MY_TEAMS &&
        state.originalTemplatesList?.filter(t => !t.caTemplateInd)?.length === 0))
);

const myTeamsTemplatesSelector = createSelector(templateLibraryFeatureSelector, state =>
  filter(state.originalTemplatesList || [], (template: ReportTemplate) => !template.caTemplateInd)
);

export const isMaximumTemplatesLimitReached = createSelector(
  myTeamsTemplatesSelector,
  templateLibraryFeatureSelector,
  (myTeamsTemplates, state) =>
    (myTeamsTemplates && myTeamsTemplates.length >= state.allowedTemplatesPerAccount) || false
);

export const allowedTemplatesPerAccount = createSelector(
  templateLibraryFeatureSelector,
  state => state.allowedTemplatesPerAccount
);

export const templatesLimitAlmostReached = createSelector(
  myTeamsTemplatesSelector,
  templateLibraryFeatureSelector,
  (myTeamsTemplates, state) => myTeamsTemplates.length >= state.allowedTemplatesPerAccount - 1
);

export const getCreateReportSectionSettings = createSelector(
  templateLibraryFeatureSelector,
  state => state.createReportSectionSettings
);

export const getTemplateListFailure = createSelector(
  templateLibraryFeatureSelector,
  state => state.loadingState === LoadStatus.Failed
);

export const isCombinedOwnershipEnabled = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.showCombinedOwnership
);

export const isNotesFeatureEnabled = createSelector(
  templateLibraryFeatureSelector,
  (state: TemplateLibraryState) => state.showNotesFeature
);
